<script context="module" lang="ts">
	import WYSIWYG from './../components/inputs/WYSIWYG.svelte';
	import type { SectionConfig } from '$lib/util/pagebuilder';
	import { gql } from 'graphql-request';
	import Media from '$lib/components/media/Media.svelte';
	import Section from '$lib/components/layout/Section.svelte';
	import { page } from '$app/stores';

	export const config: SectionConfig = {
		collection: 'section_blog_hero',
		availableFor: ['pages'],
		query: gql`
			translations {
				languages_code {
					code
				}
				title
				text
			}
            blog_post {
                preview_image {
                    ...Media
                }
                translations {
                    languages_code {
                        code
                    }
                    title
                    preview_text
					slug
                }
                    
            }
 
    `
	};

	// optional: define a type for the props (note: injectTranslations is applied)
	type Props = {
		title: string;
		text: string;
		subtitle: string;
		description: string;
		media: any;
		link: any;
		blog_post: {
			preview_image: string;
			title: string;
			preview_text: string;
			slug: string;
		};
	};
</script>

<script lang="ts">
	import Wysiwyg from '$lib/components/inputs/WYSIWYG.svelte';
	import Text from '$lib/components/Text.svelte';

	export let data: Props; // the components gets all data via this prop (additionally each top level prop is directly provided to the component)

	let specialPageSlug: string;
	$: specialPageSlug = getCurrentPrefixSlug($page.params.lang);

	function getCurrentPrefixSlug(currentLang: string) {
		for (const specialPage of $page.data.specialPagesData) {
			if (specialPage.name === 'blog') {
				for (const lang of specialPage.translations) {
					if (lang.languages_code === currentLang) {
						return lang.slug;
					}
				}
			}
		}
		return '';
	}
</script>

<Section gapType="margin" type="full" class="blog-hero">
	<div class="grid">
		<div class="col1">
			<div class="text-wrapper">
				<div class="title">{data.title}</div>
				<div class="divider" />
				<div class="text"><Wysiwyg text={data.text} /></div>
			</div>
		</div>
		<div class="col2">
			<a href={'/' + $page.params.lang + '/' + specialPageSlug + '/' + data?.blog_post?.slug}>
				<div class="image">
					<Media {...data.blog_post.preview_image} fit="cover" transform="xl" />
				</div>
				<div class="content">
					<div class="blog-title">
						{data.blog_post.title}
					</div>
					<div class="blog-text">
						<Wysiwyg text={data.blog_post.preview_text} />
					</div>
					<div class="read-more-text">
						<Text key="read_more" />
					</div>
				</div>
			</a>
		</div>
	</div>
</Section>

<style lang="scss">
	:global(.blog-hero) {
		margin-bottom: 0 !important;
	}
	:global(.blog-hero .inner-section) {
		display: static;
	}
	.grid {
		display: grid;
		grid-template-columns: 2fr 1fr;
		background: color(white);
		@media (max-width: 1100px) {
			grid-template-columns: 1fr 1fr;
		}
		@include media(phone) {
			grid-template-columns: 1fr;
		}
	}
	.col1 {
		display: flex;
		justify-content: center;
		align-items: center;
		flex-direction: column;
		padding: v(content-padding);
		height: 100%;

		@include media(phone) {
			align-items: flex-start;
		}
	}
	.title {
		@include font(h1);
		padding-bottom: v(content-gap);
	}
	.divider {
		width: 2.8rem;
		height: 3px;
		background: color(black);
		margin-top: -0.1rem;
	}
	.text {
		padding-top: v(content-gap-sm);
		@include font(text-lg);
		max-width: 25ch;
	}
	.col2 {
		position: relative;
	}
	.image {
		position: relative;
		padding: v(content-padding);
		transition: all 0.3s ease;
		filter: brightness(0.7);
		aspect-ratio: 16 / 9;
	}
	.content {
		position: absolute;
		bottom: v(content-padding-2xl);
		inset-inline-start: v(content-padding-2xl);
		color: color(white);
		max-width: 300px;
		@media (max-width: 1100px) {
			bottom: v(content-gap-xl);
			inset-inline-start: v(content-gap-xl);
		}

		.blog-title {
			@include font(h4);
			margin-bottom: v(content-gap-xs);
			flex-wrap: wrap;
		}
		.blog-text {
			@include font(text-sm);
			margin-bottom: v(content-gap-xs);
			flex-wrap: wrap;
		}
	}
	.read-more-text {
		text-transform: uppercase;
		@include font(h6);
	}
</style>
